import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Lock, User } from 'lucide-react';
import api from '../api/client';
import { useAuthStore } from '../store/auth';
import toast from 'react-hot-toast';

export default function ProfilePage() {
  const { user: storeUser, setAuth } = useAuthStore();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm: '' });

  const { data } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get('/auth/me'),
  } as any);

  const user = (data as any)?.data?.user;
  const [form, setForm] = useState({ full_name: '', email: '' });

  React.useEffect(() => {
    if (user) setForm({ full_name: user.full_name || '', email: user.email || '' });
  }, [data]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/auth/me', form);
      toast.success('პროფილი განახლდა!');
      const token = localStorage.getItem('liber_token');
      if (token) setAuth(token, { ...storeUser, full_name: form.full_name, email: form.email });
      qc.invalidateQueries({ queryKey: ['me'] });
    } catch { toast.error('შეცდომა'); }
    finally { setSaving(false); }
  };

  const handlePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (pwdForm.new_password !== pwdForm.confirm) return toast.error('პაროლები არ ემთხვევა');
    if (pwdForm.new_password.length < 6) return toast.error('პაროლი მინიმუმ 6 სიმბოლო');
    setSavingPwd(true);
    try {
      await api.put('/auth/me/password', { current_password: pwdForm.current_password, new_password: pwdForm.new_password });
      toast.success('პაროლი შეიცვალა!');
      setPwdForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'შეცდომა');
    } finally { setSavingPwd(false); }
  };

  const inp = (k: string) => (e: any) => setForm(f => ({...f, [k]: e.target.value}));
  const inpStyle = {width:'100%',background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:8,padding:'9px 12px',fontSize:13,color:'var(--text-secondary)',outline:'none',boxSizing:'border-box' as any};

  return (
    <div style={{padding:24,fontFamily:'system-ui,sans-serif',maxWidth:600}}>
      <h1 style={{fontSize:20,fontWeight:600,color:'var(--text-primary)',margin:'0 0 24px'}}>ჩემი პროფილი</h1>

      <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden',marginBottom:12}}>
        <div style={{padding:'14px 16px',borderBottom:'1px solid #1e2d45',display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:28,height:28,borderRadius:8,background:'var(--accent-bg)',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <User size={15} style={{color:'var(--accent)'}} />
          </div>
          <span style={{fontSize:13,fontWeight:500,color:'var(--text-secondary)'}}>პირადი ინფორმაცია</span>
        </div>
        <form onSubmit={handleSave} style={{padding:16}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
            <div style={{padding:'10px 12px',background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:8}}>
              <div style={{fontSize:11,color:'var(--text-faint)',marginBottom:3}}>მომხმარებელი</div>
              <div style={{fontSize:13,color:'var(--text-muted)'}}>{user?.username}</div>
            </div>
            <div style={{padding:'10px 12px',background:'var(--bg-input)',border:'1px solid #1e2d45',borderRadius:8}}>
              <div style={{fontSize:11,color:'var(--text-faint)',marginBottom:3}}>როლი</div>
              <div style={{fontSize:13,color:'var(--text-muted)'}}>{user?.role_id === 1 ? 'Admin' : user?.role_id === 2 ? 'Librarian' : 'Staff'}</div>
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <label style={{display:'block',fontSize:12,color:'var(--text-muted)',marginBottom:6}}>სახელი გვარი</label>
            <input value={form.full_name} onChange={inp('full_name')} placeholder="სახელი გვარი" style={inpStyle} />
          </div>
          <div style={{marginBottom:16}}>
            <label style={{display:'block',fontSize:12,color:'var(--text-muted)',marginBottom:6}}>ელ-ფოსტა</label>
            <input type="email" value={form.email} onChange={inp('email')} placeholder="mail@example.com" style={inpStyle} />
          </div>
          <div style={{display:'flex',justifyContent:'flex-end'}}>
            <button type="submit" disabled={saving}
              style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:12,fontWeight:500,cursor:'pointer',opacity:saving?0.6:1}}>
              <Save size={13} /> {saving ? 'შენახვა...' : 'შენახვა'}
            </button>
          </div>
        </form>
      </div>

      <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
        <div style={{padding:'14px 16px',borderBottom:'1px solid #1e2d45',display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:28,height:28,borderRadius:8,background:'var(--danger-bg)',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <Lock size={15} style={{color:'var(--danger)'}} />
          </div>
          <span style={{fontSize:13,fontWeight:500,color:'var(--text-secondary)'}}>პაროლის შეცვლა</span>
        </div>
        <form onSubmit={handlePassword} style={{padding:16}}>
          <div style={{marginBottom:12}}>
            <label style={{display:'block',fontSize:12,color:'var(--text-muted)',marginBottom:6}}>მიმდინარე პაროლი</label>
            <input type="password" value={pwdForm.current_password}
              onChange={e => setPwdForm(f => ({...f, current_password: e.target.value}))}
              placeholder="••••••••" style={inpStyle} />
          </div>
          <div style={{marginBottom:12}}>
            <label style={{display:'block',fontSize:12,color:'var(--text-muted)',marginBottom:6}}>ახალი პაროლი</label>
            <input type="password" value={pwdForm.new_password}
              onChange={e => setPwdForm(f => ({...f, new_password: e.target.value}))}
              placeholder="მინიმუმ 6 სიმბოლო" style={inpStyle} />
          </div>
          <div style={{marginBottom:16}}>
            <label style={{display:'block',fontSize:12,color:'var(--text-muted)',marginBottom:6}}>გაიმეორე პაროლი</label>
            <input type="password" value={pwdForm.confirm}
              onChange={e => setPwdForm(f => ({...f, confirm: e.target.value}))}
              placeholder="••••••••" style={inpStyle} />
          </div>
          <div style={{display:'flex',justifyContent:'flex-end'}}>
            <button type="submit" disabled={savingPwd}
              style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',background:'#7f1d1d',border:'none',borderRadius:8,color:'white',fontSize:12,fontWeight:500,cursor:'pointer',opacity:savingPwd?0.6:1}}>
              <Lock size={13} /> {savingPwd ? 'შეცვლა...' : 'პაროლის შეცვლა'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
